import { makeAutoObservable, runInAction } from 'mobx'
import { api } from '@/shared/api/endpoints'
import type { WithdrawalResponse, WithdrawalStatus } from '@/shared/api/types'
import { coinOf } from '@/shared/lib/currencies'
import { explain } from '@/shared/lib/errors'
import { Resource } from '@/stores/lib/Resource'
import type { CabinetStore } from '@/stores/CabinetStore'
import type { UiStore } from '@/stores/UiStore'

const PAGE = 20

/**
 * Статусы, в которых заявку ещё можно отозвать.
 *
 * Ровно те же два, что и на бекенде (`CANCELABLE_WITHDRAWAL_STATUSES`).
 * Разойдясь, фронт либо покажет кнопку «Отменить» на заявке, которую
 * подписант уже взял в работу — и человек решит, что деньги остановлены,
 * когда они уже в сети, — либо спрячет кнопку там, где отмена ещё
 * возможна, и вернуть свои деньги будет нечем.
 */
const CANCELABLE: ReadonlySet<WithdrawalStatus> = new Set<WithdrawalStatus>([
  'pending_approval', 'queued',
])

export function isCancelable(status: WithdrawalStatus): boolean {
  return CANCELABLE.has(status)
}

/** Как называется статус для человека и каким цветом. */
export const WITHDRAWAL_VIEW: Record<WithdrawalStatus, { text: string; tone: string }> = {
  pending_approval: { text: 'Ждёт одобрения', tone: 'warn' },
  queued: { text: 'В очереди', tone: 'warn' },
  signing: { text: 'Подписывается', tone: 'info' },
  signed: { text: 'Подписана', tone: 'info' },
  sent: { text: 'Отправлена в сеть', tone: 'info' },
  confirmed: { text: 'Выполнена', tone: 'ok' },
  failed: { text: 'Не отправилась', tone: 'bad' },
  canceled: { text: 'Отменена вами', tone: 'soft' },
  rejected: { text: 'Отклонена', tone: 'bad' },
}

/**
 * Заявки на вывод.
 *
 * Отдельный экран, а не строки в общей истории: в истории вывод виден
 * как совершённая операция, а здесь важно другое — что с ним происходит
 * прямо сейчас и можно ли ещё передумать. Пока заявка ждёт одобрения,
 * деньги уже списаны с доступного баланса, и человек, не знающий,
 * что её можно отозвать, просто ждёт.
 */
export class WithdrawalsStore {
  readonly page: Resource<{ items: WithdrawalResponse[]; total: number }>
  offset = 0
  /** Заявка, по которой спрашиваем подтверждение отмены. */
  canceling: WithdrawalResponse | null = null
  busy = false

  private readonly cabinet: CabinetStore
  private readonly ui: UiStore

  constructor(cabinet: CabinetStore, ui: UiStore) {
    this.cabinet = cabinet
    this.ui = ui
    this.page = new Resource(async () => {
      const data = await api.money.withdrawals({ limit: PAGE, offset: this.offset })
      return { items: data.items, total: data.total }
    })
    makeAutoObservable<this, 'cabinet' | 'ui'>(this, { cabinet: false, ui: false })
  }

  get limit(): number {
    return PAGE
  }

  get items(): WithdrawalResponse[] {
    return this.page.value?.items ?? []
  }

  goToOffset(offset: number): void {
    this.offset = offset
    void this.page.load()
  }

  ask(withdrawal: WithdrawalResponse): void {
    this.canceling = withdrawal
  }

  close(): void {
    if (this.busy) return
    this.canceling = null
  }

  async cancel(): Promise<void> {
    const target = this.canceling
    if (target === null || this.busy) return
    this.busy = true
    try {
      await api.money.cancelWithdrawal(target.id)
      runInAction(() => { this.canceling = null })
      this.ui.success('Заявка отменена')
    } catch (cause) {
      this.ui.failure(explain(cause, coinOf(target.currency).ticker))
    } finally {
      // Список и балансы перечитываются в любом случае: заявку мог
      // одобрить администратор секунду назад, и оставить её на экране
      // как отменяемую — значит звать нажать «Отменить» ещё раз.
      await Promise.all([
        this.page.load(),
        this.cabinet.balances.load(),
        this.cabinet.overview.load(),
      ])
      runInAction(() => { this.busy = false })
    }
  }
}
